import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Route, Switch } from 'react-router'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { ButtonDesigns, ButtonTypes } from 'src/components/Button/Button'
import LayoutShell, {
  LayoutShellButton,
} from 'src/components/LayoutShell/LayoutShell'
import NotFoundBackgroundPageContent from 'src/components/NotFoundBackgroundPageContent/NotFoundBackgroundPageContent'
import { SubnavDesign, SubnavOption } from 'src/components/Subnav/Subnav'
import { useTranslate } from 'src/localization/Trns'
import PeopleAppRoute from 'src/navigation/appRoutes/people.appRoute'
import { Rdrct } from 'src/navigation/Lnk'
import {
  invitePeopleUrl,
  peopleInvitedToOrgUrl,
  peopleMembersUrl,
} from 'src/navigation/urls'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserExplicitOrganizationListModelFromState } from 'src/redux/selectors/data/organizations.selectors'
import {
  selectCalculatedBackgroundLocation,
  selectPeopleInvitedToOrgMatch,
  selectPeopleMembersMatch,
} from 'src/redux/selectors/router.selectors'

const People: React.FC = () => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )
  const peopleMembersMatch = useSelector(selectPeopleMembersMatch)
  const peopleInvitedToOrgMatch = useSelector(selectPeopleInvitedToOrgMatch)
  const currentUser = useSelector(selectUserListModelFromState)
  const organization = useSelector(
    selectUserExplicitOrganizationListModelFromState,
  )

  const subnavOptions: SubnavOption[] = useMemo(() => {
    const baseOptions: SubnavOption[] =
      currentUser?.isInExplicitOrg &&
      (organization?.memberListVisible ||
        currentUser?.hasOrgCardManagerPermissions)
        ? [
            {
              label: translate('Organization Members'),
              to: peopleMembersUrl(),
              isActive: peopleMembersMatch !== null,
              ariaLabelSource: 'Organization Members',
            },
          ]
        : []

    return currentUser?.hasOrgAdminPermissions
      ? [
          ...baseOptions,
          {
            label: translate('Invited To Org'),
            to: peopleInvitedToOrgUrl(),
            isActive: peopleInvitedToOrgMatch !== null,
            ariaLabelSource: 'Invited To Org',
          },
        ]
      : baseOptions
  }, [
    peopleMembersMatch,
    peopleInvitedToOrgMatch,
    currentUser,
    organization,
    translate,
  ])

  const inviteAnalyticsEvent = peopleMembersMatch
    ? 'organization members - invite people - text link press'
    : peopleInvitedToOrgMatch
      ? 'invited to org - invite people - text link press'
      : 'invite people - text link press'

  const buttons: LayoutShellButton[] | undefined =
    currentUser?.hasOrgAdminPermissions
      ? [
          {
            design: ButtonDesigns.designCta,
            type: ButtonTypes.button,
            text: translate('Invite People'),
            testId: 'invitePeople',
            linkProps: {
              to: invitePeopleUrl(),
              ariaLabel: 'Invite People',
            },
            onClick: (): void => {
              analytics?.track(inviteAnalyticsEvent, {
                intent: 'invite people',
                interaction: 'button press',
              })
            },
          },
        ]
      : undefined

  return (
    <LayoutShell
      title={translate('People')}
      subnavDesign={SubnavDesign.Floating}
      subnavOptions={currentUser?.isInExplicitOrg ? subnavOptions : []}
      buttons={buttons}
      content={
        <Switch location={calculatedBackgroundLocation}>
          {PeopleAppRoute.subRoutes.map((route, i) => (
            <Route key={i} {...route.routeProps}>
              {route.component}
            </Route>
          ))}
          <Route {...PeopleAppRoute.routeProps} exact>
            <Rdrct to={peopleMembersUrl()} />
          </Route>
          <Route path={'/'}>
            <NotFoundBackgroundPageContent />
          </Route>
        </Switch>
      }
    />
  )
}

export default People
